/**
 * Reverse-geocode helper — turn lat/lng into a human-readable address.
 *
 * Backed by OpenStreetMap Nominatim (free, no API key). Used by the Settings
 * page Location card so the "Use my current location" button can populate
 * the Address field after capturing GPS coords AND forward the postal code
 * to the household PATCH payload (T12.0.15.2 — Flyer Deals precondition).
 *
 * Design notes:
 * - Returns `null` on any failure (network, non-200, malformed response,
 *   timeout, missing `display_name`). Callers proceed with a coords-only
 *   save when null — geocoding loss is a soft failure, never a save blocker.
 * - On success, returns `{ displayName, postalCode }`. `postalCode` is
 *   `undefined` when Nominatim's response had no `address.postcode`
 *   (rare — overseas, oceans, sparse OSM coverage). Callers send the
 *   postal code on the household PATCH only when it's present.
 * - 5-second `AbortController` timeout. Nominatim can be slow under load and
 *   we don't want the geolocation handler to hang indefinitely.
 * - This helper is off the coordinate-save critical path. Browser JavaScript
 *   cannot set `User-Agent`, so callers must treat rate limiting as a soft
 *   enrichment failure rather than a reason to delay location capture.
 */

const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
const TIMEOUT_MS = 5000;

export interface ReverseGeocodeResult {
  displayName: string;
  /**
   * Canadian FSA + LDU postal code extracted from Nominatim's
   * `address.postcode`. `undefined` when the response had no `postcode`
   * field (rare — overseas, oceans, sparse OSM coverage).
   */
  postalCode?: string;
}

export async function reverseGeocode(
  lat: number,
  lon: number,
): Promise<ReverseGeocodeResult | null> {
  const params = new URLSearchParams({
    lat: String(lat),
    lon: String(lon),
    format: 'json',
    zoom: '18',
    addressdetails: '1',
  });
  const url = `${NOMINATIM_REVERSE_URL}?${params.toString()}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const resp = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
      signal: controller.signal,
    });

    if (!resp.ok) {
      return null;
    }

    const data: unknown = await resp.json();
    if (
      !data ||
      typeof data !== 'object' ||
      !('display_name' in data) ||
      typeof (data as { display_name: unknown }).display_name !== 'string'
    ) {
      return null;
    }
    const displayName = (data as { display_name: string }).display_name;

    // Best-effort: pull `address.postcode` when present + a non-empty string.
    // Nominatim returns `address` as a nested object when `addressdetails=1`
    // (which we always send above). Missing/non-string postcode collapses
    // to the `postalCode` key being absent on the result (matches
    // `exactOptionalPropertyTypes` — never `{ postalCode: undefined }`).
    const address = (data as { address?: unknown }).address;
    if (address && typeof address === 'object' && 'postcode' in address) {
      const raw = (address as { postcode: unknown }).postcode;
      if (typeof raw === 'string' && raw.trim().length > 0) {
        return { displayName, postalCode: raw };
      }
    }

    return { displayName };
  } catch {
    // Network errors, abort/timeout, JSON parse failures — all collapse to
    // null. The caller falls back to coords-only save.
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}
